import { useState, useEffect } from "react";

const decodeBase64 = (str) => {
  if (!str) return "";
  try {
    const cleanStr = str.trim().replace(/\s/g, "");
    if (!/^[a-zA-Z0-9+/=-_]+$/.test(cleanStr)) {
      return str;
    }
    const standardBase64 = cleanStr.replace(/-/g, "+").replace(/_/g, "/");
    return decodeURIComponent(
      atob(standardBase64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
  } catch (e) {
    try {
      const cleanStr = str
        .trim()
        .replace(/\s/g, "")
        .replace(/-/g, "+")
        .replace(/_/g, "/");
      return atob(cleanStr);
    } catch (err) {
      return str;
    }
  }
};

const unescapeHtmlIfNeeded = (html) => {
  if (!html) return "";
  if (
    /&lt;\s*(div|p|br|span|html|body|table|tr|td|blockquote|a|h[1-6])/i.test(
      html,
    )
  ) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || html;
  }
  return html;
};

const HtmlRenderer = ({ fileUrl, attachments }) => {
  const [htmlContent, setHtmlContent] = useState("");
  const [loading, setLoading] = useState(true);

  const updateImageSources = (html, attachments) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const images = tempDiv.querySelectorAll("img");

    images.forEach((img) => {
      const src = img.getAttribute("src");

      if (src && src.startsWith("cid:")) {
        const fileName = src.replace("cid:", "");

        const matchedFile = attachments.find(
          (att) => att.attachmentName === fileName,
        );

        if (matchedFile) {
          img.setAttribute("src", matchedFile.fileUrl);
        }
      }
    });

    return tempDiv.innerHTML;
  };

  useEffect(() => {
    if (!fileUrl) {
      setLoading(false);
      return;
    }
    const fetchHtml = async () => {
      setLoading(true);
      try {
        const response = await fetch(fileUrl);
        const text = await response.text();
        const decodedText = decodeBase64(text);

        // Unescape doubly-escaped HTML if needed
        const unescapedText = unescapeHtmlIfNeeded(decodedText);

        // Escape raw email addresses inside angle brackets (e.g. <email@domain.com> -> &lt;email@domain.com&gt;)
        // so the browser parses them as text nodes instead of unknown HTML tags.
        const escapedText = unescapedText.replace(
          /<([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})>/gi,
          "&lt;$1&gt;",
        );

        const formattedHtml = updateImageSources(
          escapedText,
          attachments || [],
        );
        setHtmlContent(formattedHtml);
      } catch (error) {
        console.error("Error fetching HTML file:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHtml();
  }, [fileUrl, attachments]);

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-8 tw-gap-2.5 tw-text-slate-400 tw-animate-pulse">
        <i className="las la-spinner tw-animate-spin tw-text-xl tw-text-theme" />
        <span className="tw-text-xs tw-font-semibold tw-tracking-wide">
          Loading email content...
        </span>
      </div>
    );
  }

  return (
    <div
      className="tw-break-words tw-max-w-full tw-overflow-auto"
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

export default HtmlRenderer;
